import { Copy } from "lucide-react"
import { useEffect, useState } from "react"
import { cn } from "../../utils/cn"

type State = "open" | "sent" | "preview" | "ending" | "ended" | "live"
interface Preview {
	state: State
	endsAt?: number
	paidAt?: number
	hasLink: boolean
	firstOpenedAt: number | null
	extensions: Array<{ at: number; days: number }>
}

const LABEL: Record<State, string> = {
	open: "No preview: the brand page is open to everyone",
	sent: "Founder link made, not opened yet",
	preview: "In preview",
	ending: "Preview ending soon",
	ended: "Preview ended: customers see a resting page",
	live: "Live (paid)",
}
const when = (t: number) => new Date(t).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })

// Search admin: the brand page preview, from private founder link to paid.
export function BrandPreviewSettings({ getAuthHeader }: { getAuthHeader: () => Promise<Record<string, string>> }) {
	const base = `${import.meta.env.VITE_BACKEND_URL || ""}/api/v1/search/config/brand-preview`
	const [preview, setPreview] = useState<Preview | null>(null)
	const [link, setLink] = useState("")
	const [busy, setBusy] = useState("")
	const [message, setMessage] = useState<{ error: boolean; text: string } | null>(null)

	async function request(path = "", method = "GET", body?: unknown) {
		const response = await fetch(`${base}${path}`, {
			method,
			headers: { "Content-Type": "application/json", ...(await getAuthHeader()) },
			...(body ? { body: JSON.stringify(body) } : {}),
		})
		const result = await response.json()
		if (!response.ok) throw new Error(result.error || "Request failed")
		return result
	}
	const load = () => request().then(setPreview)
	useEffect(() => {
		load().catch((error) => setMessage({ error: true, text: error.message }))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	async function run(label: string, action: () => Promise<string>) {
		setBusy(label)
		setMessage(null)
		try {
			const text = await action()
			await load()
			setMessage({ error: false, text })
		} catch (error) {
			setMessage({ error: true, text: error instanceof Error ? error.message : "Something went wrong" })
		} finally {
			setBusy("")
		}
	}
	const makeLink = () =>
		run("link", async () => {
			const result = await request("/link", "POST")
			setLink(result.founderUrl)
			return "New founder link made. Any earlier link stops working."
		})
	const extend = (days: number) =>
		run("extend", async () => {
			await request("/extend", "POST", { days })
			return `Extended by ${days} days`
		})
	const setPaid = (paid: boolean) =>
		run("paid", async () => {
			await request("/paid", "POST", { paid })
			return paid ? "Marked paid: the page is live" : "Payment removed"
		})

	const button = "px-3 py-1.5 border border-border rounded-md hover:bg-accent text-sm disabled:opacity-50"
	return (
		<section className="bg-card border border-border rounded-lg p-6 mb-6">
			<h2 className="text-lg font-semibold mb-1">Brand page preview</h2>
			<p className="text-sm text-muted-foreground mb-3">
				Send the founder their private link. The 48 hours start when they first open it.
			</p>
			{preview && (
				<div className="text-sm space-y-3">
					<p>
						<b className="font-medium">{LABEL[preview.state]}</b>
						{preview.endsAt && <span className="text-muted-foreground"> · ends {when(preview.endsAt)}</span>}
						{preview.paidAt && <span className="text-muted-foreground"> · since {when(preview.paidAt)}</span>}
					</p>
					{link && (
						<div className="flex gap-2 items-center">
							<input aria-label="Founder link" className="flex-1 px-2 py-1 border border-border rounded bg-background text-xs" readOnly value={link} />
							<button className={button} onClick={() => navigator.clipboard?.writeText(link)} type="button">
								<Copy className="w-3 h-3" />
							</button>
						</div>
					)}
					<div className="flex flex-wrap gap-2">
						<button className={button} disabled={!!busy} onClick={makeLink}>
							{preview.hasLink ? "Make a new founder link" : "Make founder link"}
						</button>
						{preview.hasLink && preview.state !== "live" && (
							<>
								<button className={button} disabled={!!busy} onClick={() => extend(2)}>
									Extend 2 days
								</button>
								<button className={button} disabled={!!busy} onClick={() => extend(7)}>
									Extend 7 days
								</button>
							</>
						)}
						{preview.hasLink && (
							<button className={button} disabled={!!busy} onClick={() => setPaid(preview.state !== "live")}>
								{preview.state === "live" ? "Undo paid" : "Mark paid"}
							</button>
						)}
					</div>
					{preview.extensions.length > 0 && (
						<p className="text-xs text-muted-foreground">
							Extended {preview.extensions.length}× ({preview.extensions.map((e) => `${e.days}d`).join(", ")})
						</p>
					)}
				</div>
			)}
			{message && <p className={cn("text-sm mt-3", message.error ? "text-red-600" : "text-green-700")}>{message.text}</p>}
		</section>
	)
}
